import { useState, useEffect, useRef, useCallback, useMemo, RefObject } from 'react';
import { createPortal } from 'react-dom';
import './OrderOnboarding.css';

interface OnboardingStep {
  id: string;
  ref: RefObject<HTMLElement> | null;
  text: string;
  isPause?: boolean;
  isModal?: boolean;
  tabSwitch?: 'menu' | 'order';
  showDot?: boolean;
}

interface OrderOnboardingProps {
  statusBarRef: RefObject<HTMLDivElement>;
  ownUpsellsRef: RefObject<HTMLDivElement>;
  venueUpsellsRef: RefObject<HTMLDivElement>;
  menuTabRef: RefObject<HTMLButtonElement>;
  passportRef: RefObject<HTMLButtonElement>;
  hasVenueCards: boolean;
  hasOwnCards: boolean;
  orderStatus: string;
  onSwitchToMenu: () => void;
  onSwitchToOrder: () => void;
  onComplete: () => void;
}

export default function OrderOnboarding({
  statusBarRef,
  ownUpsellsRef,
  venueUpsellsRef,
  menuTabRef,
  passportRef,
  hasVenueCards,
  hasOwnCards,
  orderStatus,
  onSwitchToMenu,
  onSwitchToOrder,
  onComplete,
}: OrderOnboardingProps) {
  // Build resolved steps array once at mount — memoized to prevent stale closures
  const steps = useMemo<OnboardingStep[]>(() => {
    const result: OnboardingStep[] = [];
    result.push({
      id: 'status',
      ref: statusBarRef,
      text: '\u0417\u0434\u0435\u0441\u044c \u043e\u0442\u043e\u0431\u0440\u0430\u0436\u0430\u0435\u0442\u0441\u044f \u0441\u0442\u0430\u0442\u0443\u0441 \u0432\u0430\u0448\u0435\u0433\u043e \u0437\u0430\u043a\u0430\u0437\u0430. \u041e\u0442\u0434\u044b\u0445\u0430\u0439\u0442\u0435 \u2014 \u043c\u044b \u0441\u043e\u043e\u0431\u0449\u0438\u043c, \u043a\u043e\u0433\u0434\u0430 \u0432\u0441\u0451 \u0431\u0443\u0434\u0435\u0442 \u0433\u043e\u0442\u043e\u0432\u043e.',
      showDot: true,
    });
    if (hasOwnCards) {
      result.push({
        id: 'own-upsells',
        ref: ownUpsellsRef,
        text: '\u0414\u043e\u0431\u0430\u0432\u044c\u0442\u0435 \u0447\u0442\u043e-\u043d\u0438\u0431\u0443\u0434\u044c \u043a \u0437\u0430\u043a\u0430\u0437\u0443, \u043f\u043e\u043a\u0430 \u043e\u043d \u0433\u043e\u0442\u043e\u0432\u0438\u0442\u0441\u044f. \u0412\u0441\u0451 \u0432\u044b\u0434\u0430\u0434\u0443\u0442 \u0432\u043c\u0435\u0441\u0442\u0435 \u2014 \u043d\u0435 \u043f\u0440\u0438\u0434\u0451\u0442\u0441\u044f \u0432\u043e\u0437\u0432\u0440\u0430\u0449\u0430\u0442\u044c\u0441\u044f \u0434\u0432\u0430\u0436\u0434\u044b.',
        showDot: true,
      });
    }
    if (hasVenueCards) {
      result.push({
        id: 'venue-upsells',
        ref: venueUpsellsRef,
        text: '\u041f\u043e\u0437\u0438\u0446\u0438\u0438 \u0438\u0437 \u0434\u0440\u0443\u0433\u0438\u0445 \u0440\u0435\u0441\u0442\u043e\u0440\u0430\u043d\u043e\u0432, \u043a\u043e\u0442\u043e\u0440\u044b\u0435 \u0445\u043e\u0440\u043e\u0448\u043e \u0441\u043e\u0447\u0435\u0442\u0430\u044e\u0442\u0441\u044f \u0441 \u0432\u0430\u0448\u0438\u043c \u0437\u0430\u043a\u0430\u0437\u043e\u043c.',
        showDot: true,
      });
    }
    result.push({
      id: 'menu-tab',
      ref: menuTabRef,
      text: '\u041f\u043e\u043b\u043d\u043e\u0435 \u043c\u0435\u043d\u044e \u0440\u0435\u0441\u0442\u043e\u0440\u0430\u043d\u0430 \u2014 \u0435\u0441\u043b\u0438 \u0445\u043e\u0442\u0438\u0442\u0435 \u0432\u044b\u0431\u0440\u0430\u0442\u044c \u0447\u0442\u043e-\u0442\u043e \u0435\u0449\u0451.',
      tabSwitch: 'menu',
      showDot: true,
    });
    result.push({
      id: 'passport',
      ref: passportRef,
      text: '\u0412\u0430\u0448 \u0424\u0443\u0434 \u041f\u0430\u0441\u043f\u043e\u0440\u0442 \u2014 \u043d\u0430\u0433\u0440\u0430\u0434\u044b, \u0431\u043e\u043d\u0443\u0441\u044b \u0438 \u0441\u043a\u0438\u0434\u043a\u0438 \u0437\u0430 \u043a\u0430\u0436\u0434\u043e\u0435 \u043f\u043e\u0441\u0435\u0449\u0435\u043d\u0438\u0435. \u0414\u043e\u0431\u0430\u0432\u043b\u044f\u0439\u0442\u0435 \u0434\u0440\u0443\u0437\u0435\u0439, \u0434\u0430\u0440\u0438\u0442\u0435 \u0438 \u043f\u043e\u043b\u0443\u0447\u0430\u0439\u0442\u0435 \u043f\u043e\u0434\u0430\u0440\u043a\u0438. \u041d\u0430\u0436\u043c\u0438\u0442\u0435, \u0447\u0442\u043e\u0431\u044b \u0437\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043e\u0432\u0430\u0442\u044c\u0441\u044f.',
      tabSwitch: 'order',
      showDot: true,
    });
    result.push({
      id: 'pwa-modal',
      ref: null,
      text: '',
      isModal: true,
      showDot: false,
    });
    return result;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [currentIndex, setCurrentIndex] = useState(0);
  const [cutoutRect, setCutoutRect] = useState<DOMRect | null>(null);
  const [cutoutRadius, setCutoutRadius] = useState(16);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState(1);
  const [showLaterBtn, setShowLaterBtn] = useState(false);
  const [installPromptAvailable, setInstallPromptAvailable] = useState(false);
  const [pwaModalReady, setPwaModalReady] = useState(false);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deferredInstallRef = useRef<{ prompt: () => void } | null>(null);
  const mountTimeRef = useRef(Date.now());

  const currentStep = steps[currentIndex];
  const dotSteps = steps.filter((s) => s.showDot);
  const currentDotIndex = dotSteps.findIndex((s) => s.id === currentStep?.id);

  // Capture beforeinstallprompt — use state flag to avoid render-time ref read race
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredInstallRef.current = e as unknown as { prompt: () => void };
      setInstallPromptAvailable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Handle order status changes mid-onboarding
  useEffect(() => {
    if (orderStatus === 'COMPLETED' || orderStatus === 'CANCELLED') {
      finishOnboarding();
    }
  }, [orderStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Position cutout on current step's ref
  const updateCutout = useCallback(() => {
    const step = steps[currentIndex];
    if (!step?.ref?.current) {
      setCutoutRect(null);
      return;
    }
    const rect = step.ref.current.getBoundingClientRect();
    setCutoutRect(rect);
    const br = parseFloat(getComputedStyle(step.ref.current).borderRadius) || 12;
    setCutoutRadius(br);
  }, [currentIndex, steps]);

  // Recalculate cutout on resize
  useEffect(() => {
    const handleResize = () => updateCutout();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateCutout]);

  const finishOnboarding = useCallback(() => {
    try { localStorage.setItem('olso-onboarding-done', '1'); } catch { /* */ }
    onComplete();
  }, [onComplete]);

  const advance = useCallback(() => {
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    // Hide tooltip BEFORE changing index — batched in React 18, prevents text flash
    setTooltipVisible(false);
    setShowLaterBtn(false);
    const nextIndex = currentIndex + 1;
    if (nextIndex >= steps.length) {
      finishOnboarding();
      return;
    }
    setCurrentIndex(nextIndex);
  }, [currentIndex, steps.length, finishOnboarding]);

  // Scroll to target and update cutout when step changes
  useEffect(() => {
    const step = steps[currentIndex];
    if (!step) return;

    // Skip venue-upsells step if order is READY (venue cards hidden)
    if (step.id === 'venue-upsells' && orderStatus === 'READY') {
      advance();
      return;
    }

    // Tab switching
    if (step.tabSwitch === 'menu') onSwitchToMenu();
    if (step.tabSwitch === 'order') onSwitchToOrder();

    // Modal step — wait 30s from mount before showing
    if (step.isModal) {
      setOverlayOpacity(0);
      setTooltipVisible(false);
      setCutoutRect(null);
      const elapsed = Date.now() - mountTimeRef.current;
      const remaining = Math.max(0, 30000 - elapsed);
      const timer = setTimeout(() => setPwaModalReady(true), remaining);
      return () => clearTimeout(timer);
    }

    const isFirstStep = currentIndex === 0;
    const hasTabSwitch = !!step.tabSwitch;
    // tooltip already hidden by advance() — ensure for first step too
    setTooltipVisible(false);

    // Passport step: let the button be clickable and finish onboarding on click
    let passportClickHandler: (() => void) | null = null;

    // Defer scroll + cutout into a single timeout chain.
    // Tab switches need extra time for React to re-render the new tab content.
    const renderDelay = hasTabSwitch ? 150 : 0;
    const renderTimer = setTimeout(() => {
      // Now React has rendered the tab switch — scroll to the element
      if (step.ref?.current) {
        const el = step.ref.current;
        const scrollParent = el.closest('.order-status-container') as HTMLElement | null;
        if (scrollParent) {
          const elRect = el.getBoundingClientRect();
          const parentRect = scrollParent.getBoundingClientRect();
          const elCenter = elRect.top - parentRect.top + scrollParent.scrollTop + elRect.height / 2;
          const targetScroll = elCenter - parentRect.height / 2;
          scrollParent.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
        } else {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      if (step.id === 'passport' && step.ref?.current) {
        passportClickHandler = () => finishOnboarding();
        step.ref.current.addEventListener('click', passportClickHandler, { once: true });
      }
    }, renderDelay);

    // After scroll settles, update cutout — CSS transitions it smoothly to new position
    const scrollDelay = isFirstStep ? 500 : (hasTabSwitch ? 550 : 400);
    const timer = setTimeout(() => {
      updateCutout();
      setOverlayOpacity(1);
      // Show tooltip after cutout CSS transition completes
      setTimeout(() => {
        setTooltipVisible(true);
        if (step.id === 'passport') {
          setTimeout(() => setShowLaterBtn(true), 1000);
        }
      }, isFirstStep ? 0 : 420);
    }, scrollDelay);

    return () => {
      clearTimeout(renderTimer);
      clearTimeout(timer);
      if (passportClickHandler && step.ref?.current) {
        step.ref.current.removeEventListener('click', passportClickHandler);
      }
    };
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOverlayClick = () => {
    // Block clicks while step is transitioning — otherwise rapid taps skip steps
    // before the user actually sees them (scroll + animation chain takes ~500-1000ms).
    if (!tooltipVisible) return;
    if (currentStep?.isPause) {
      advance();
      return;
    }
    // Passport step: overlay click still advances (only cutout area is blocked)
    advance();
  };

  // Build clip-path with rounded-corner hole matching element's border-radius
  const getClipPath = (): string => {
    if (!cutoutRect) return 'none';
    const pad = 4;
    const r = Math.min(cutoutRadius + pad, (cutoutRect.width + 2 * pad) / 2, (cutoutRect.height + 2 * pad) / 2);
    const t = cutoutRect.top - pad;
    const l = cutoutRect.left - pad;
    const b = cutoutRect.bottom + pad;
    const ri = cutoutRect.right + pad;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // SVG path: outer rect CW, bridge, inner rect CCW with rounded corners
    return `path('M 0 0 L ${vw} 0 L ${vw} ${vh} L 0 ${vh} L 0 ${t} L ${l + r} ${t} Q ${l} ${t} ${l} ${t + r} L ${l} ${b - r} Q ${l} ${b} ${l + r} ${b} L ${ri - r} ${b} Q ${ri} ${b} ${ri} ${b - r} L ${ri} ${t + r} Q ${ri} ${t} ${ri - r} ${t} L 0 ${t} Z')`;
  };

  // Tooltip position: below if space, above otherwise, clamped to viewport
  const getTooltipStyle = (): React.CSSProperties => {
    if (!cutoutRect) return { display: 'none' };
    const pad = 4;
    const tooltipHeight = 140; // approximate max height
    const margin = 12;
    const centerX = Math.min(
      Math.max((cutoutRect.left + cutoutRect.right) / 2 - 160, 16),
      window.innerWidth - 336
    );

    const spaceBelow = window.innerHeight - cutoutRect.bottom - pad;
    const spaceAbove = cutoutRect.top - pad;

    let top: number | undefined;
    let bottom: number | undefined;

    if (spaceBelow >= tooltipHeight + margin) {
      // Place below
      top = cutoutRect.bottom + pad + margin;
    } else if (spaceAbove >= tooltipHeight + margin) {
      // Place above
      bottom = window.innerHeight - cutoutRect.top + pad + margin;
    } else {
      // Not enough space either way — overlay on bottom of cutout
      top = Math.max(16, window.innerHeight - tooltipHeight - 16);
    }

    return {
      position: 'fixed' as const,
      left: centerX,
      top,
      bottom,
      zIndex: 10001,
    };
  };

  // PWA modal step — render only after 30s delay
  if (currentStep?.isModal) {
    if (!pwaModalReady) return null;
    return <PwaInstallModal
      deferredInstallRef={deferredInstallRef}
      installPromptAvailable={installPromptAvailable}
      onDismiss={finishOnboarding}
    />;
  }

  return createPortal(
    <>
      {/* Overlay with cutout */}
      <div
        className="onboarding-overlay"
        style={{
          clipPath: cutoutRect ? getClipPath() : 'none',
          opacity: overlayOpacity,
        }}
        onClick={handleOverlayClick}
      />

      {/* Cutout border-radius overlay (transparent, rounds the cutout visually) */}
      {cutoutRect && (
        <div
          className={`onboarding-cutout-border ${currentStep?.id === 'passport' ? 'onboarding-cutout-glow' : ''}`}
          style={{
            top: cutoutRect.top - 4,
            left: cutoutRect.left - 4,
            width: cutoutRect.width + 8,
            height: cutoutRect.height + 8,
            borderRadius: cutoutRadius + 4,
            opacity: overlayOpacity,
          }}
        />
      )}

      {/* Full-screen click catcher for non-passport steps — click anywhere to advance */}
      {currentStep && !currentStep.isPause && !currentStep.isModal && currentStep.id !== 'passport' && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10002,
            cursor: 'pointer',
          }}
          onClick={advance}
        />
      )}

      {/* Tooltip */}
      {tooltipVisible && currentStep && !currentStep.isPause && !currentStep.isModal && (
        <div
          className="onboarding-tooltip"
          style={getTooltipStyle()}
          onClick={currentStep.id === 'passport' ? (e) => e.stopPropagation() : undefined}
        >
          <div className="onboarding-tooltip-text">{currentStep.text}</div>

          {/* "later" button on passport step */}
          {currentStep.id === 'passport' && (
            <button
              className={`onboarding-later-btn ${showLaterBtn ? 'visible' : ''}`}
              onClick={advance}
            >
              {'\u043f\u043e\u0437\u0436\u0435'}
            </button>
          )}

          {/* Progress dots */}
          <div className="onboarding-dots">
            {dotSteps.map((s, i) => (
              <div
                key={s.id}
                className={`onboarding-dot ${i === currentDotIndex ? 'active' : ''}`}
              />
            ))}
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

// --- PWA Install Modal ---

function PwaInstallModal({
  deferredInstallRef,
  installPromptAvailable,
  onDismiss,
}: {
  deferredInstallRef: React.RefObject<{ prompt: () => void } | null>;
  installPromptAvailable: boolean;
  onDismiss: () => void;
}) {
  const isIOS = /(iPhone|iPad|iPod)/i.test(navigator.userAgent);

  // Android without beforeinstallprompt -> skip modal entirely
  useEffect(() => {
    if (!isIOS && !installPromptAvailable) {
      onDismiss();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isIOS && !installPromptAvailable) return null;

  return createPortal(
    <div className="onboarding-modal-overlay">
      <div className="onboarding-modal" onClick={(e) => e.stopPropagation()}>
        <button className="onboarding-modal-close" onClick={onDismiss}>{'\u00d7'}</button>
        <div className="onboarding-modal-icon">{'\ud83d\udcf2'}</div>
        <div className="onboarding-modal-title">
          {'\u0425\u043e\u0442\u0438\u0442\u0435 \u043f\u043e\u043b\u0443\u0447\u0430\u0442\u044c \u0443\u0432\u0435\u0434\u043e\u043c\u043b\u0435\u043d\u0438\u044f \u043e \u0437\u0430\u043a\u0430\u0437\u0435, \u0434\u0430\u0436\u0435 \u0435\u0441\u043b\u0438 \u0432\u044b \u0432 \u0434\u0440\u0443\u0433\u043e\u043c \u043f\u0440\u0438\u043b\u043e\u0436\u0435\u043d\u0438\u0438?'}
        </div>
        <div className="onboarding-modal-subtitle">
          {'\u0414\u043e\u0431\u0430\u0432\u044c\u0442\u0435 OLSO \u043d\u0430 \u0433\u043b\u0430\u0432\u043d\u044b\u0439 \u044d\u043a\u0440\u0430\u043d'}
        </div>

        {isIOS ? (
          <div className="onboarding-ios-steps">
            <div className="onboarding-ios-step">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L12 16M12 2L8 6M12 2L16 6" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 14V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V14" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{'\u041d\u0430\u0436\u043c\u0438\u0442\u0435 \u043a\u043d\u043e\u043f\u043a\u0443 \u00ab\u041f\u043e\u0434\u0435\u043b\u0438\u0442\u044c\u0441\u044f\u00bb'}</span>
            </div>
            <div className="onboarding-ios-step">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="4" stroke="#007AFF" strokeWidth="2"/>
                <path d="M12 8V16M8 12H16" stroke="#007AFF" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>{'\u0412\u044b\u0431\u0435\u0440\u0438\u0442\u0435 \u00ab\u041d\u0430 \u044d\u043a\u0440\u0430\u043d \u0414\u043e\u043c\u043e\u0439\u00bb'}</span>
            </div>
          </div>
        ) : (
          <button
            className="onboarding-modal-btn"
            onClick={() => {
              deferredInstallRef.current?.prompt();
              onDismiss();
            }}
          >
            {'\u0423\u0441\u0442\u0430\u043d\u043e\u0432\u0438\u0442\u044c'}
          </button>
        )}

        {isIOS && (
          <button className="onboarding-modal-btn" onClick={onDismiss}>
            {'\u041f\u043e\u043d\u044f\u0442\u043d\u043e'}
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
